import { memo, useMemo, useState, useEffect, useCallback } from 'react';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import _keys from 'lodash/keys';
//Local files
import { FieldsContainer } from 'components/Common/BasePageUi/BasePageUi';
import useError from 'hooks/useError';
import useSuccess from 'hooks/useSuccess';
import useUrgentlyAdditionalCosts from 'hooks/useUrgentlyAdditionalCosts';
import { urgentlyAdditionalCostTypes } from 'helpers/constants';
import { formatLabel } from 'helpers/index';
import { ERROR_MESSAGES } from 'helpers/constants';

const UrgentlyAdditionalCostForm = ({ orderId, processing, disabled, onProcessing }) => {
  const { setError } = useError();
  const { setSuccess } = useSuccess();
  const { createUrgentlyAdditionalCost } = useUrgentlyAdditionalCosts();
  const [costType, setCostType] = useState({ value: '', error: '' });
  const [price, setPrice] = useState({ value: '', error: '' });
  const [minutes, setMinutes] = useState({ value: '', error: '' });
  const [notes, setNotes] = useState({ value: '', error: '' });

  const costTypeOptions = useMemo(() => 
    _keys(urgentlyAdditionalCostTypes).map(key => ({
      value: urgentlyAdditionalCostTypes[key],
      label: formatLabel(urgentlyAdditionalCostTypes[key])
    })), []
  );

  const requiresWaitingTime = useMemo(() => 
    costType.value === urgentlyAdditionalCostTypes.WAITING_TIME, 
    [costType.value]
  );

  const clearFields = useCallback(() => {
    setCostType({ value: '', error: '' });
    setPrice({ value: '', error: '' });
    setMinutes({ value: '', error: '' });
    setNotes({ value: '', error: '' });
  }, []);

  useEffect(() => {
    if (processing) {
      let hasError = false;

      if (!costType.value) {
        setCostType(prev => ({ ...prev, error: ERROR_MESSAGES.EMPTY_ERROR_MESSAGE }));
        hasError = true;
      }
      if (!price.value || parseFloat(price.value) <= 0) {
        setPrice(prev => ({ ...prev, error: ERROR_MESSAGES.PRICE_INVALID }));
        hasError = true;
      }
      if (requiresWaitingTime && (!minutes.value || parseInt(minutes.value) <= 0)) {
        setMinutes(prev => ({ ...prev, error: ERROR_MESSAGES.MINUTES_REQUIRED }));
        hasError = true;
      }
      if (!notes.value) {
        setNotes(prev => ({ ...prev, error: ERROR_MESSAGES.EMPTY_ERROR_MESSAGE }));
        hasError = true;
      }

      if (hasError) {
        onProcessing(false);
        return;
      }

      const urgentlyAdditionalCost = {
        cost_type: costType.value,
        price: parseFloat(price.value),
        notes: notes.value,
        ...(requiresWaitingTime && { minutes: parseInt(minutes.value) })
      };

      createUrgentlyAdditionalCost({ order_id: orderId, urgently_additional_cost: urgentlyAdditionalCost })
        .then(() => setSuccess('Additional cost request created and submitted to Urgently').then(clearFields))
        .catch((error) => setError(error))
        .finally(() => onProcessing(false));
    }
  }, [processing, clearFields, costType.value, createUrgentlyAdditionalCost, minutes.value, notes.value, onProcessing, orderId, price.value, requiresWaitingTime, setError, setSuccess]);

  useEffect(() => {
    return clearFields;
  }, [clearFields]);

  return (
    <FieldsContainer sx={{ mt: 2 }}>
      <TextField 
        label='Cost Type' 
        value={costType.value} 
        select
        helperText={costType.error} 
        error={!!costType.error} 
        onChange={({ target: { value } }) => setCostType({ value, error: '' })} 
        disabled={disabled}
        SelectProps={{ MenuProps: { anchorOrigin: { vertical: 'bottom', horizontal: 'center' }, sx: { height: '40%' } } }}
      >
        {costTypeOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField 
        label='Price ($)' 
        value={price.value} 
        helperText={price.error} 
        error={!!price.error} 
        onChange={({ target: { value } }) => setPrice({ value, error: '' })} 
        InputProps={{ type: 'number', inputProps: { min: 0, step: 0.01 } }}
        disabled={disabled}
      />

      {requiresWaitingTime && (
        <TextField 
          label='Minutes' 
          value={minutes.value} 
          helperText={minutes.error} 
          error={!!minutes.error} 
          onChange={({ target: { value } }) => setMinutes({ value, error: '' })} 
          InputProps={{ type: 'number', inputProps: { min: 1 } }}
          disabled={disabled}
        />
      )}

      <TextField 
        label='Notes'
        multiline
        rows={3}
        inputProps={{ maxLength: 255 }} 
        value={notes.value} 
        helperText={notes.error} 
        error={!!notes.error} 
        onChange={({ target: { value } }) => setNotes({ value, error: '' })}
        disabled={disabled}
      />
    </FieldsContainer>
  );
};

export default memo(UrgentlyAdditionalCostForm);
